import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import { ArrowRight, HandCoins, Sparkles, TrendingUp } from "lucide-react";
import { trackEstimatorInteract, trackEstimatorApplyClick } from "@/lib/estimatorAnalytics";

const MIN = 5000;
const MAX = 250000;
const STEP = 500;
const RATIO = 0.75;

const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function AdvanceEstimator() {
  const nav = useNavigate();
  const [net, setNet] = useState(25000);

  const advance = useMemo(() => Math.round(net * RATIO), [net]);

  const onNetChange = (value) => {
    setNet(value);
    trackEstimatorInteract(value, Math.round(value * RATIO));
  };

  const apply = () => {
    trackEstimatorApplyClick(net, advance);
    const q = new URLSearchParams({
      net: String(net),
      advance: String(advance),
      src: "estimator",
    });
    nav(`/apply?${q.toString()}`);
  };

  return (
    <section
      className="py-20 gradient-champagne border-y border-[#E8DFD8]"
      data-testid="advance-estimator-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="lux-card p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left — Copy */}
          <div className="lg:col-span-5">
            <span className="overline flex items-center gap-2">
              <Sparkles size={14} strokeWidth={1.5} /> Instant Estimator
            </span>
            <h2 className="font-serif-lux text-4xl sm:text-5xl mt-4 text-[#2A1C14] leading-[1.05]">
              See your <em className="italic text-[#5A3B31]">advance</em> in seconds.
            </h2>
            <p className="mt-5 text-[#5A3B31] leading-relaxed">
              Drag the slider to your expected net commission. We'll show your maximum advance —
              up to 75% of what you've earned — before you even fill out a single field.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-[#2A1C14]">
              {[
                "No credit check",
                "No obligation — estimate only",
                "Same-day review when you apply",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#CBA4A4]" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Right — Estimator */}
          <div className="lg:col-span-7">
            <div
              className="rounded-2xl bg-[#FCFAF8] border border-[#E8DFD8] p-8 shadow-[0_20px_60px_rgba(42,28,20,0.08)]"
              data-testid="advance-estimator-card"
            >
              <div className="flex items-baseline justify-between">
                <label className="overline text-[11px]" htmlFor="net-commission-input">
                  Expected Net Commission
                </label>
                <span className="text-xs text-[#8C7A70]">
                  {fmt(MIN)} – {fmt(MAX)}+
                </span>
              </div>

              <div className="mt-3 flex items-center gap-3">
                <span className="font-serif-lux text-5xl text-[#2A1C14]">$</span>
                <input
                  id="net-commission-input"
                  data-testid="estimator-net-input"
                  type="number"
                  min={MIN}
                  step={STEP}
                  value={net}
                  onChange={(e) => {
                    const v = Math.max(MIN, Math.min(MAX * 4, Number(e.target.value) || 0));
                    onNetChange(v);
                  }}
                  className="font-serif-lux text-5xl bg-transparent focus:outline-none w-full text-[#2A1C14]"
                />
              </div>

              <div className="mt-6">
                <Slider
                  data-testid="estimator-slider"
                  value={[Math.min(net, MAX)]}
                  onValueChange={(v) => onNetChange(v[0])}
                  min={MIN}
                  max={MAX}
                  step={STEP}
                  className="[&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:bg-[#2A1C14] [&_[role=slider]]:border-[#2A1C14] [&>span:first-child]:bg-[#F3D8DA] [&>span:first-child>span]:bg-[#2A1C14]"
                />
                <div className="flex justify-between text-[11px] text-[#8C7A70] mt-2 tracking-wider uppercase">
                  <span>{fmt(MIN)}</span>
                  <span>{fmt(MAX / 2)}</span>
                  <span>{fmt(MAX)}+</span>
                </div>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="rounded-xl bg-white border border-[#E8DFD8] p-5">
                  <p className="overline text-[10px] flex items-center gap-1.5">
                    <TrendingUp size={12} strokeWidth={1.5} /> Advance Rate
                  </p>
                  <p className="font-serif-lux text-3xl mt-2 text-[#5A3B31]">75%</p>
                  <p className="text-[11px] text-[#8C7A70] mt-1">of your net commission</p>
                </div>
                <div
                  className="rounded-xl bg-[#2A1C14] text-[#FCFAF8] p-5 relative overflow-hidden"
                  data-testid="estimator-result-card"
                >
                  <p className="overline text-[10px] text-[#CBA4A4] flex items-center gap-1.5">
                    <HandCoins size={12} strokeWidth={1.5} /> You Could Receive
                  </p>
                  <p
                    className="font-serif-lux text-3xl sm:text-4xl mt-2 text-[#F3D8DA]"
                    data-testid="estimator-advance-value"
                  >
                    {fmt(advance)}
                  </p>
                  <p className="text-[11px] text-[#CBA4A4] mt-1">
                    est. same-day when approved
                  </p>
                </div>
              </div>

              <button
                onClick={apply}
                className="btn-primary w-full justify-center mt-6"
                data-testid="estimator-apply-cta"
              >
                Apply for {fmt(advance)} <ArrowRight size={18} strokeWidth={1.5} />
              </button>
              <p className="text-[11px] text-[#8C7A70] mt-3 text-center leading-relaxed">
                Estimator is illustrative only. Final advance amount is subject to review,
                verification, and approval.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
